// A password field with an eye button to show or hide what is typed (both apps, every password field). The field
// keeps its name, value and validation; only its type changes, so password managers and the form see the same input.
import { useId, useState, type InputHTMLAttributes } from 'react';

// Material "visibility" / "visibility_off"
const EYE =
  'M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z';
const EYE_OFF =
  'M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46C3.08 8.3 1.78 10.02 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78l3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z';

export function PasswordInput({ style, ...input }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [shown, setShown] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'block', flex: style?.flex ?? undefined }}>
      <input
        {...input}
        type={shown ? 'text' : 'password'}
        style={{ ...style, width: '100%', boxSizing: 'border-box', paddingRight: 46 }}
      />
      <button
        type="button"
        aria-label={shown ? 'Hide password' : 'Show password'}
        aria-pressed={shown}
        title={shown ? 'Hide password' : 'Show password'}
        onClick={() => setShown((now) => !now)}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 44,
          display: 'grid',
          placeItems: 'center',
          padding: 0,
          background: 'none',
          border: 0,
          color: 'inherit',
          opacity: 0.7,
          cursor: 'pointer',
        }}
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <path d={shown ? EYE_OFF : EYE} fill="currentColor" />
        </svg>
      </button>
    </span>
  );
}

/**
 * A labelled password field: the label, then the input with its eye. The eye sits outside the label on purpose — a
 * button inside a label becomes part of the input's name ("Password Show password" to a screen reader).
 * `className` is the class the field's own <label> used to carry, so the layout is the one the page had.
 */
export function PasswordField({
  label,
  className,
  ...input
}: { label: string; className?: string } & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'>) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} style={{ display: 'contents' }}>
        <span>{label}</span>
      </label>
      <PasswordInput id={id} {...input} />
    </div>
  );
}
